import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import SEO, { attorneyJsonLd } from "@/components/SEO";
import { Eyebrow, PrimaryCTA, SectionNum } from "@/components/ui-bits";
import { ATTORNEYS } from "@/data/attorneys";
import { ArrowUpRight } from "lucide-react";

export default function People() {
  return (
    <Layout>
      <SEO
        title="People"
        description="Shelton Law PLLC is built around senior attorneys who run their own matters. Each attorney of record is the attorney on the file."
        path="/people"
        jsonLd={ATTORNEYS.length === 1 ? attorneyJsonLd(ATTORNEYS[0]) : undefined}
      />
      {/* Hero */}
      <section className="border-b border-hairline" data-testid="people-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-20 pb-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-8">
              <Eyebrow>The Firm</Eyebrow>
              <h1 className="display-1 text-ink mt-6 max-w-4xl">Attorneys.</h1>
              <p className="mt-8 text-ink-soft text-[1.0625rem] leading-relaxed max-w-2xl">
                Shelton Law PLLC is built around senior attorneys who run their own matters. Each attorney of record is the attorney on the file.
              </p>
            </div>
            <div className="lg:col-span-4 lg:text-right">
              <p className="section-num">
                {String(ATTORNEYS.length).padStart(2, "0")} · Attorneys of record
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Roster — grid, one card per attorney; scales cleanly to N > 1 */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-20" data-testid="people-roster">
        <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-16">
          {ATTORNEYS.map((a, i) => (
            <AttorneyCard key={a.slug} attorney={a} num={String(i + 1).padStart(2, "0")} />
          ))}
        </ul>
      </section>

      {/* Careers hook temporarily hidden per firm direction. */}
    </Layout>
  );
}

function AttorneyCard({ attorney, num }) {
  return (
    <li data-testid={`attorney-card-${attorney.slug}`} className="group">
      <Link to={attorney.bioHref} className="block">
        <div className="aspect-[4/5] overflow-hidden border border-hairline bg-ivory-alt">
          <img
            src={attorney.photo}
            alt={attorney.name}
            className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-[filter] duration-500"
          />
        </div>
        <div className="mt-5 flex items-baseline justify-between">
          <SectionNum>{num}</SectionNum>
          <span className="text-[10px] uppercase tracking-[0.24em] text-brass-deep font-semibold">{attorney.role}</span>
        </div>
        <h3 className="font-serif text-3xl text-ink mt-2 leading-tight group-hover:text-brass transition-colors">
          {attorney.name}
        </h3>
        <p className="mt-4 text-ink-soft text-[0.95rem] leading-relaxed">
          {attorney.bio}
        </p>

        <div className="mt-6 border-t border-hairline pt-4">
          <p className="text-[10px] uppercase tracking-[0.24em] text-ink-soft font-semibold mb-2">Practices</p>
          <p className="text-ink text-sm leading-relaxed">
            {attorney.practices.slice(0, 4).join(" · ")}
            {attorney.practices.length > 4 ? ` · +${attorney.practices.length - 4} more` : ""}
          </p>
        </div>

        <div className="mt-4">
          <p className="text-[10px] uppercase tracking-[0.24em] text-ink-soft font-semibold mb-2">Admissions</p>
          <p className="text-ink text-sm leading-relaxed">{attorney.admissions.join(", ")}</p>
        </div>

        {attorney.email && (
          <div className="mt-4">
            <p className="text-[10px] uppercase tracking-[0.24em] text-ink-soft font-semibold mb-2">Direct</p>
            <a
              href={`mailto:${attorney.email}`}
              onClick={(e) => e.stopPropagation()}
              className="text-ink text-sm hover:text-brass transition-colors break-all"
              data-testid={`attorney-email-${attorney.slug}`}
            >
              {attorney.email}
            </a>
          </div>
        )}

        <div className="mt-6 inline-flex items-center gap-2 text-ink border-b border-brass pb-1 text-[11px] tracking-[0.24em] uppercase font-semibold group-hover:text-brass transition-colors">
          Full Biography <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={2} />
        </div>
      </Link>
    </li>
  );
}
